import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { mabarService, PublicMabarEvent } from '@/services/mabarService';
import communityService, { MyCommunityItem } from '@/services/communityService';
import { Button } from '@/components/shadcn/button';
import { formatLocalEventTimeRange } from '@/utils/dateUtils';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
  'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des',
];

const DAY_SHORT = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

const formatEventPrice = (ev: PublicMabarEvent): { label: string; perPerson: boolean } => {
  if (ev.type === 'free') return { label: 'Gratis', perPerson: false };

  const roles = ev.roles_availability || [];
  if (roles.length === 0) return { label: 'Gratis', perPerson: false };

  const prices = roles
    .map((r) => r.price)
    .filter((p) => p !== undefined && p !== null && !isNaN(p));

  if (prices.length === 0) return { label: 'Gratis', perPerson: false };

  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  if (minPrice === 0 && maxPrice === 0) return { label: 'Gratis', perPerson: false };

  const formatIDR = (val: number) =>
    new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(val);

  if (minPrice === maxPrice) {
    return { label: formatIDR(minPrice), perPerson: true };
  }

  return { label: `${formatIDR(minPrice)} - ${formatIDR(maxPrice)}`, perPerson: true };
};

export const MabarTerdekatList: React.FC<{ refreshKey?: number }> = ({ refreshKey = 0 }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<PublicMabarEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJoinedCommunityEvents = async () => {
      try {
        setLoading(true);

        // 1. Fetch communities current user has joined
        const myCommRes = await communityService.getMyCommunities();
        const myCommunities = myCommRes?.data || [];

        if (myCommunities.length === 0) {
          setEvents([]);
          return;
        }

        const joinedSlugs = new Set(
          myCommunities.map((c: MyCommunityItem) => (c.slug || '').toLowerCase()).filter(Boolean)
        );
        const joinedNames = new Set(
          myCommunities.map((c: MyCommunityItem) => (c.name || '').toLowerCase()).filter(Boolean)
        );

        // 2. Fetch public mabar events
        const res = await mabarService.getPublicEvents({ page: 1, limit: 50, lp: 'true' });
        const rawEvents = res?.data || [];

        // 3. Filter only events belonging to joined communities
        const filtered = rawEvents.filter((ev) => {
          const hostSlug = (ev.host?.slug || '').toLowerCase();
          const hostName = (ev.host?.name || '').toLowerCase();
          return (
            (hostSlug && joinedSlugs.has(hostSlug)) ||
            (hostName && joinedNames.has(hostName))
          );
        });

        setEvents(filtered.slice(0, 3));
      } catch (err) {
        console.error('Failed to load nearby mabar events from joined communities:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchJoinedCommunityEvents();
  }, [refreshKey]);

  const parseDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return { mon: 'Agu', day: '02', dayShort: 'Min' };
      return {
        mon: MONTH_NAMES[d.getMonth()] || 'Agu',
        day: String(d.getDate()).padStart(2, '0'),
        dayShort: DAY_SHORT[d.getDay()] || 'Min',
      };
    } catch {
      return { mon: 'Agu', day: '02', dayShort: 'Min' };
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-[#0E1726]">Mabar Terdekat</h3>
        <button
          type="button"
          onClick={() => navigate('/discovery/event')}
          className="text-xs font-semibold text-[#FC480D] hover:underline cursor-pointer"
        >
          Lihat Semua
        </button>
      </div>

      {/* List / Cards */}
      {loading ? (
        <div className="flex flex-col gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="bg-gray-100 animate-pulse rounded-3xl h-44 w-full" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-3xl p-6 text-center border border-[#E0E6ED] shadow-2xs flex flex-col items-center justify-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-orange-50 text-[#FC480D] flex items-center justify-center border border-orange-100 shadow-xs">
            <Icon icon="ph:calendar-x-bold" className="w-6 h-6" />
          </div>
          <div className="space-y-1 max-w-xs">
            <p className="text-sm font-bold text-[#0E1726]">Belum Ada Mabar Terdekat</p>
            <p className="text-xs text-gray-500 font-medium leading-relaxed">
              Belum ada jadwal mabar dari komunitas yang kamu ikuti. Yuk cari event seru lainnya di sekitar kamu!
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/discovery/event')}
            className="mt-1 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FC480D] hover:bg-[#D83B08] active:scale-95 text-white text-xs font-bold transition-all shadow-xs cursor-pointer"
          >
            <Icon icon="ph:magnifying-glass-bold" className="w-4 h-4 text-white" />
            <span>Cari Event</span>
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {events.map((ev) => {
            const { mon, day, dayShort } = parseDate(ev.event_date);
            const sportName = ev.sport_category?.name || 'Olahraga';
            const filledSlots = ev.filled_slots || 0;
            const totalSlots = ev.total_slots || 12;
            const availableSlots = ev.available_slots ?? Math.max(0, totalSlots - filledSlots);
            const fillPct = Math.min(100, Math.round((filledSlots / totalSlots) * 100));

            const isFull = availableSlots <= 0 || fillPct >= 100;
            const isLowSlots = availableSlots <= 3 && availableSlots > 0;

            const formattedTime = formatLocalEventTimeRange(ev.start_time, ev.end_time) || '16.00 - 18.00 WIB';
            const venueText = ev.lokasi_arena ? `${ev.lokasi_arena}${ev.city ? ` - ${ev.city}` : ''}` : ev.city || 'Lokasi belum diatur';
            const priceInfo = formatEventPrice(ev);

            return (
              <div
                key={ev.slug || ev.name}
                onClick={() => {
                  if (ev.slug) {
                    navigate(`/event/${ev.slug}`);
                  } else {
                    navigate('/event');
                  }
                }}
                className="bg-white rounded-3xl p-4 sm:p-5 border border-[#E0E6ED] shadow-2xs hover:shadow-xs transition-shadow space-y-3.5 cursor-pointer"
              >
                {/* Top Badges Row */}
                <div className="flex items-center justify-between gap-2">
                  <span className="bg-orange-50 border border-orange-100 text-[#FC480D] font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1.5">
                    <Icon icon="ph:soccer-ball-bold" className="w-3.5 h-3.5" />
                    <span>{sportName}</span>
                  </span>

                  {isLowSlots ? (
                    <span className="bg-rose-50 border border-rose-100 text-rose-600 font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1">
                      <Icon icon="ph:fire-bold" className="w-3.5 h-3.5" />
                      <span>Cepat penuh</span>
                    </span>
                  ) : isFull ? (
                    <span className="bg-red-50 border border-red-100 text-red-600 font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1">
                      <Icon icon="ph:warning-circle-bold" className="w-3.5 h-3.5" />
                      <span>Slot penuh</span>
                    </span>
                  ) : (
                    <span className="bg-emerald-50 border border-emerald-100 text-emerald-600 font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1">
                      <Icon icon="ph:check-circle-bold" className="w-3.5 h-3.5" />
                      <span>Slot tersedia</span>
                    </span>
                  )}
                </div>

                {/* Main Details Grid */}
                <div className="flex items-start gap-3.5">
                  {/* Left Date Badge Box */}
                  <div className="w-14 h-16 rounded-2xl bg-orange-50/60 border border-orange-100/80 flex flex-col items-center justify-center shrink-0">
                    <span className="text-[11px] font-bold text-[#FC480D] uppercase leading-tight">
                      {mon}
                    </span>
                    <span className="text-xl font-extrabold text-[#0E1726] leading-none my-0.5">
                      {day}
                    </span>
                    <span className="text-[10px] font-semibold text-gray-400 capitalize leading-tight">
                      {dayShort}
                    </span>
                  </div>

                  {/* Right Event Info */}
                  <div className="space-y-1.5 flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-[#0E1726] truncate">
                      {ev.name}
                    </h3>

                    <div className="space-y-1 text-xs text-gray-500 font-medium">
                      <div className="flex items-center gap-1.5 truncate">
                        <Icon icon="ph:clock-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="truncate">{formattedTime}</span>
                      </div>

                      <div className="flex items-center gap-1.5 truncate">
                        <Icon icon="ph:map-pin-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="truncate">{venueText}</span>
                      </div>

                      <div className="flex items-center gap-1.5 truncate">
                        <Icon icon="ph:tag-bold" className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <span className="font-bold text-[#0E1726]">
                          {priceInfo.label}{' '}
                          {priceInfo.perPerson && (
                            <span className="font-normal text-gray-400 text-[11px]">per orang</span>
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Slot Progress Bar Section */}
                    <div className="space-y-1 pt-1">
                      <div className="flex items-center justify-between text-[11px] font-semibold">
                        <span className="text-gray-400">
                          {filledSlots}/{totalSlots} terisi
                        </span>
                        <span className="text-[#FC480D]">
                          {isFull ? 'Slot Penuh' : `Sisa ${availableSlots} slot`}
                        </span>
                      </div>
                      <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#FC480D] rounded-full transition-all duration-300"
                          style={{ width: `${fillPct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Host & Action Footer */}
                <div className="border-t border-gray-100 pt-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {ev.host?.avatar ? (
                      <img
                        src={ev.host.avatar}
                        alt={ev.host.name}
                        className="w-8 h-8 rounded-full object-cover shrink-0 border border-gray-100"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#FC480D] text-white font-bold text-xs flex items-center justify-center shrink-0">
                        {(ev.host?.name || 'S').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-xs font-bold text-[#0E1726] truncate">
                      {ev.host?.name || 'Sporto Community'}
                    </span>
                  </div>

                  {ev.is_joined || ev.isJoined || ev.is_host ? (
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (ev.slug) {
                          navigate(`/event/${ev.slug}`);
                        } else {
                          navigate('/event');
                        }
                      }}
                      className="h-9 px-4 rounded-full bg-[#0E1726] hover:bg-[#1C2838] text-white text-xs font-bold shadow-2xs transition-all active:scale-98 cursor-pointer flex items-center gap-1.5 shrink-0"
                    >
                      <Icon icon="ph:eye-bold" className="w-3.5 h-3.5" />
                      <span>Detail</span>
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (ev.slug) {
                          navigate(`/event/${ev.slug}`);
                        } else {
                          navigate('/event');
                        }
                      }}
                      className="h-9 px-4 rounded-full bg-[#FC480D] hover:bg-[#D83B08] text-white text-xs font-bold shadow-2xs transition-all active:scale-98 cursor-pointer flex items-center gap-1.5 shrink-0"
                    >
                      <Icon icon="ph:sign-in-bold" className="w-3.5 h-3.5" />
                      <span>Gabung</span>
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MabarTerdekatList;
